import React, { Fragment } from 'react';
import styled from 'styled-components';
import { shape, func } from 'prop-types';
import Scrollbars from 'react-custom-scrollbars';
import I10n from '../features/i10n';
import Configuration from '../models/Configuration';
import { possible_starter_selections, consumptionWarningPercent, mc80_range_id } from '../constants';
import MsxIcons from '../features/msx-icons';
import { formatLengthUnit } from '../utils/data-formatter';
import Gauges from './gauges';
import InformationIcon from '../atoms/information-icon';
import DropdownMenu from './dropdown_menu';
import { SECONDARY_COLOR } from '../color-constants';

const ScrollbarContainer = styled.div`
  display: flex;
  flex: 1 1 0px;
  height: 100%;
  @media (max-width: 800px) {
    display: none;
  }
`;

const MobileContainer = styled.div`
  display: none;
  flex-direction: column;
  flex: 1 1 0px;
  @media (max-width: 800px) {
    display: flex;
  }
`;

const Container = styled.div`
  min-height: 100%;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  padding-right: 5px;
  padding-left: 5px;
`;

const ConfigurationInformation = styled.div`
  padding: 6px;
  display: flex;
  flex-direction: ${props => props.orientation ? props.orientation : 'column'};
  justify-content: space-between;
  border-bottom: 1px solid #e7e6e6;
  flex-wrap: wrap;
`;

const ConfigurationInformationTitle = styled.span`
  display: inline-flex;
  align-items: center;
  flex: 1 1 auto;
  min-width: 86px;
  font-size: 12px;
  line-height: 14px;
  color: #333333;
  font-weight: bold;
`;

const ConfigurationInformationValue = styled.span`
  display: flex;
  justify-content: flex-end;
  font-size: 12px;
  line-height: 14px;
  color: #333333;
  white-space: nowrap;
`;

const BlockInformationWrapper = styled.div`
  display: block;
  padding: 0 8px;
  margin-right: -6px;
  font-size: 12px;
  line-height: 14px;
  color: #333333;
`;

const BlockInformationRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  padding: 4px 0px;
  justify-content: space-between;
`;

const ConsumptionInformationRow = styled.div`
  padding: 4px 0px;
`;

const BlockInformationCell = styled.span`
  display: block;
  font-size: 12px;
  line-height: 14px;
  text-align: left;
  white-space: nowrap;
  min-width: 50px;
`;

const PacBlockInformationCell = styled(BlockInformationCell)`
  width: 50px;
  white-space: normal;
`;

const BlockInformationSubRow = styled(BlockInformationRow)`
  flex-wrap: nowrap;
  width: 100%;
  padding-left: 4px;
`;

const BlockInformationName = styled(BlockInformationCell)`
  font-weight: bold;
  white-space: unset;
  max-width: 100px;
`;

const BlockInformationValue = styled(BlockInformationCell)`
  text-align: right;
  margin-left: 5px;
`;

const ActionIcon = styled.span`
  margin-left: 5px;
  display: inline-block;
  cursor: pointer;
`;

const IconWrapper = styled.div`
  cursor: pointer;
  color: ${props => props.color};
`;

const WarningWrapper = styled.div`
  width: 150px;
  position: relative;
  padding: 15px;
  font-weight: normal;
`;

const Title = styled.div`
  margin: 0px;
  font-size: 20px !important;
  line-height: 36px !important;
  font-weight: bold;
  color: #626469;
  @media (max-width: 800px) {
    line-height: inherit;
  }
  white-space: nowrap;
`;

const consumptionStatus = {
  error: {
    color: 'red',
    key: 'configuration.general.consumption.error'
  },
  warning: {
    color: '#E2B80F',
    key: 'configuration.general.consumption.warning'
  },
  normal: {
    color: SECONDARY_COLOR,
    key: ''
  }
}

class ConfigurationInformations extends React.Component {

  static propTypes = {
    configuration: shape(Configuration),
    onConsumptionDisplayChange: func,
    onRenderDimensionsChange: func,
  }

  constructor(props) {
    super(props);
    this.state = {
      consumptionShown: false,
      dimensionsShown: false,
    };
  }

  get consumptionVoltages() {
    return ['5', '24'];
  }

  setWrapperRef = element => {
    this.wrapperElement = element;
  };

  setMobileWrapperRef = element => {
    this.mobileWrapperElement = element;
  };

  processElectricalValue = (electricalValue, shouldScale = false) => {

    // the "+"" before the second return ensure that no useless 0 will be added as decimal if they are not needed
    return shouldScale ? (electricalValue / 1000.0).toFixed(2) : +electricalValue.toFixed(2);
  }

  /**
   * Render the consumption information only if any max consumption of the configuration
   * is above 0.
   * @param {*} maxConsumptions
   */
  _shouldRenderConsumption = (maxConsumptions) => {
    return maxConsumptions.filter(consumption => consumption > 0).length > 0;
  }

  _computeGaugeStatus = (cons, maxCons) => {
    return (cons > maxCons ? consumptionStatus.error : (maxCons > 0 && cons <= maxCons && cons * (1 + consumptionWarningPercent) >= maxCons ? consumptionStatus.warning : consumptionStatus.normal));
  }

  _computeBusesWorstStatus = (powerSupplyBuses) => {
    let worstStatus = consumptionStatus.normal;

    for (let i = 0; i < powerSupplyBuses.length && worstStatus.color !== consumptionStatus.error.color; i++) {
      let buses = powerSupplyBuses[i];
      for (let j = 0; j < buses.length && worstStatus.color !== consumptionStatus.error.color; j++) {
        const bus = buses[j];
        const cons = bus.consumption, maxCons = bus.maxConsumption;
        const busStatus = this._computeGaugeStatus(cons, maxCons);
        if (busStatus.color !== consumptionStatus.normal.color) {
          worstStatus = busStatus;
        }
      }
    }

    return worstStatus;
  }


  /**
   * Returns true if the configuration has consumption error and if the 24V sensors error is the unique failing reason.
  */
   hasConsumptionOnlyDueTo24VSensors = () => {
    let hasOtherConsumptionError = false;

    let consumptions = [this.props.configuration.pacConsumptionBlock?.consumption3Dot3V, this.props.configuration.pacConsumptionBlock?.consumption24V, (this.props.configuration.pacConsumptionBlock?.consumption3Dot3V / 1000.0 * 3.3) + (this.props.configuration.pacConsumptionBlock?.consumption24V / 1000.0 * 24.0), (this.props.configuration.pacConsumptionBlock?.consumption3Dot3V / 1000.0 * 3.3) + (this.props.configuration.pacConsumptionBlock?.consumption24V / 1000.0 * 24.0) + (this.props.configuration.pacConsumptionBlock?.consumption24VSensors / 1000.0 * 24.0)];
    let maxConsumptions = [this.props.configuration.pacConsumptionBlock?.maxConsumption3Dot3V, this.props.configuration.pacConsumptionBlock?.maxConsumption24V, this.props.configuration.pacConsumptionBlock?.maxComsumption3Dot3and24V, this.props.configuration.pacConsumptionBlock?.maxGlobalConsumption];

    for (let i = 0; i < consumptions.length && !hasOtherConsumptionError; i++) {
      if (consumptions[i] > maxConsumptions[i]) {
        hasOtherConsumptionError = true;
      }
    }

    if (hasOtherConsumptionError) {
      return false;
    }

    return this.props.configuration.pacConsumptionBlock?.consumption24VSensors > this.props.configuration.pacConsumptionBlock?.maxConsumption24VSensors;
  }

  _computePacWorstStatus = (conf) => {
    let worstStatus = consumptionStatus.normal;

    let consumptions = [
      conf.pacConsumptionBlock?.consumption3Dot3V,
      conf.pacConsumptionBlock?.consumption24V,
      conf.pacConsumptionBlock?.consumption24VSensors,
      (conf.pacConsumptionBlock?.consumption3Dot3V / 1000.0 * 3.3) + (conf.pacConsumptionBlock?.consumption24V / 1000.0 * 24.0),
      (conf.pacConsumptionBlock?.consumption3Dot3V / 1000.0 * 3.3) + (conf.pacConsumptionBlock?.consumption24V / 1000.0 * 24.0) + (conf.pacConsumptionBlock?.consumption24VSensors / 1000.0 * 24.0)
    ];
    let maxConsumptions = [
      conf.pacConsumptionBlock?.maxConsumption3Dot3V,
      conf.pacConsumptionBlock?.maxConsumption24V,
      conf.pacConsumptionBlock?.maxConsumption24VSensors,
      conf.pacConsumptionBlock?.maxComsumption3Dot3and24V,
      conf.pacConsumptionBlock?.maxGlobalConsumption
    ];

    for (let i = 0; i < consumptions.length && worstStatus.color !== consumptionStatus.error.color; i++) {
      const status = this._computeGaugeStatus(consumptions[i], maxConsumptions[i]);
      if (status.color !== consumptionStatus.normal.color) {
        worstStatus = status;
      }
    }

    return worstStatus;

  }

  renderPacConsumptionGauge = (title, cons, maxCons) => {

    const shouldScaleConsumption = (maxCons !== null && maxCons >= 10000.0);
    const consumptionUnit = shouldScaleConsumption ? 'A' : 'mA';

    return (
      <BlockInformationSubRow>
        <PacBlockInformationCell>{I10n.get(title)}</PacBlockInformationCell>
        <Gauges currentValue={cons} maxValue={maxCons} color={this._computeGaugeStatus(cons, maxCons).color} text={`${this.processElectricalValue(cons, shouldScaleConsumption)} ${I10n.get('unit.' + consumptionUnit, 'data')}`}></Gauges>
        <BlockInformationValue>
          {this.processElectricalValue(maxCons, shouldScaleConsumption)} {I10n.get('unit.' + consumptionUnit, 'data')}
        </BlockInformationValue>
      </BlockInformationSubRow>
    );
  }

  renderPacWattConsumptionGauge = (title, cons, maxCons) => {
    return (
      <BlockInformationSubRow>
        <PacBlockInformationCell>{I10n.get(title)}</PacBlockInformationCell>
        <Gauges currentValue={cons} maxValue={maxCons} color={this._computeGaugeStatus(cons, maxCons).color} text={`${this.processElectricalValue(cons, false)} ${I10n.get('unit.W', 'data')}`}></Gauges>
        <BlockInformationValue>
          {this.processElectricalValue(maxCons, false)} {I10n.get('unit.W', 'data')}
        </BlockInformationValue>
      </BlockInformationSubRow>
    );
  }

  renderPACWidths = () => {
    return (<>
      <BlockInformationRow>
        <BlockInformationName>{I10n.get('configuration.general.pac_rack.local')}</BlockInformationName>
        <BlockInformationValue>
          <BlockInformationSubRow>
            <BlockInformationCell>&nbsp;</BlockInformationCell>
            <BlockInformationValue>{formatLengthUnit(this.props.configuration?.rack?.width || this.props.configuration.headModule?.width || 0)}</BlockInformationValue>
          </BlockInformationSubRow>
        </BlockInformationValue>
      </BlockInformationRow>
      {this.props.configuration.localRacks?.map((rackConfiguration, idx) => {
        return (
          <BlockInformationRow key={idx}>
            <BlockInformationName>{I10n.getAndReplace('configuration.general.pac_rack.extension', idx + 1)}</BlockInformationName>
            <BlockInformationValue>
              <BlockInformationSubRow>
                <BlockInformationCell>&nbsp;</BlockInformationCell>
                <BlockInformationValue>{formatLengthUnit(rackConfiguration.rack?.width || rackConfiguration.headModule?.width || 0)}</BlockInformationValue>
              </BlockInformationSubRow>
            </BlockInformationValue>
          </BlockInformationRow>
        );
      })}
    </>);
  }

  renderPACStatusMessage = (status) => {
    return I10n.getOrDefault(`${status.key}.pac`, I10n.get(status.key));
  }

  renderPACConsumption = (conf, rackIdx, wrapperElement) => {
    const worstStatus = this._computePacWorstStatus(conf);

    return (
      <ConfigurationInformation>
        <ConfigurationInformationTitle>
          {rackIdx > 0 ? I10n.getAndReplace('configuration.general.consumption_local_rack', rackIdx) : I10n.getAndReplace('configuration.general.consumption')}
          {worstStatus.color !== consumptionStatus.normal.color && <IconWrapper color={worstStatus.color}>
            <DropdownMenu
              data-clickable
              toggleElement={<InformationIcon size="small"></InformationIcon>}
              containerElement={<WarningWrapper>{this.hasConsumptionOnlyDueTo24VSensors() ? I10n.get('configuration.pac.consumption_24v_sensors.error') : this.renderPACStatusMessage(worstStatus)}</WarningWrapper>}
              boundingElement={wrapperElement}
            ></DropdownMenu>
          </IconWrapper>}
        </ConfigurationInformationTitle>
        <BlockInformationWrapper>
          {this.renderPacWattConsumptionGauge(`configuration.general.consumption.x80.global`, ((conf.pacConsumptionBlock?.consumption3Dot3V / 1000.0 * 3.3) + (conf.pacConsumptionBlock?.consumption24V / 1000.0 * 24.0) + (conf.pacConsumptionBlock?.consumption24VSensors / 1000.0 * 24.0)) || 0, conf.pacConsumptionBlock?.maxGlobalConsumption)}
          {this.renderPacConsumptionGauge(`configuration.general.consumption.x80.3_3V`, conf.pacConsumptionBlock?.consumption3Dot3V || 0,conf.pacConsumptionBlock?.maxConsumption3Dot3V || 0)}
          {this.renderPacConsumptionGauge(`configuration.general.consumption.x80.24V`, conf.pacConsumptionBlock?.consumption24V || 0, conf.pacConsumptionBlock?.maxConsumption24V || 0)}
          {this.renderPacConsumptionGauge(`configuration.general.consumption.x80.24V_sensors`, conf.pacConsumptionBlock?.consumption24VSensors || 0, conf.pacConsumptionBlock?.maxConsumption24VSensors || 0)}
          {this.renderPacWattConsumptionGauge(`configuration.general.consumption.x80.3_3v_24v`, ((conf.pacConsumptionBlock?.consumption3Dot3V || 0) / 1000.0 * 3.3) + ((conf.pacConsumptionBlock?.consumption24V || 0) / 1000.0 * 24.0), conf.pacConsumptionBlock?.maxComsumption3Dot3and24V || 0)}
        </BlockInformationWrapper>
      </ConfigurationInformation>
    );

  }

  renderPACConsumptions = (wrapperElement) => {
    return (
      <>
        {this.renderPACConsumption(this.props.configuration, 0, wrapperElement)}
        {this.props.configuration.localRacks?.map((rackConfiguration, idx) => {
          return (
            <Fragment key={idx}>
              {this.renderPACConsumption(rackConfiguration, (idx + 1), wrapperElement)}
            </Fragment>
          );
        })}
      </>
    );
  }

  renderNumberIOModulesForConfig = (config, rackIdx) => {
    const maxIOModules = config.starterType === possible_starter_selections.PAC ? (config?.rack?.maxIoModules || config.rangeInfo?.maxTotalIOModules) : config.rangeInfo?.maxTotalIOModules || 0;

    return (
      <>
        {maxIOModules > 0 && <BlockInformationRow>
          <BlockInformationName>{rackIdx > 0 ? I10n.getAndReplace('configuration.general.pac_rack.extension', rackIdx) : I10n.getAndReplace('configuration.general.pac_rack.local')}</BlockInformationName>
          <BlockInformationValue>
            <BlockInformationSubRow>
              <BlockInformationCell>&nbsp;</BlockInformationCell>
              <BlockInformationValue>{config.ioModuleCount()} / {maxIOModules}</BlockInformationValue>
            </BlockInformationSubRow>
          </BlockInformationValue>
        </BlockInformationRow>}
      </>
    );
  }

  renderNumberIOModules = (ioModuleRangeName) => {

    if (this.props.configuration?.localRacks?.length > 0) {
      return (
        <ConfigurationInformation orientation={'column'}>
          <ConfigurationInformationTitle>{I10n.getOrDefault(`configuration.general.io_modules.${ioModuleRangeName}`, I10n.get('configuration.general.io_modules'))}</ConfigurationInformationTitle>
          <BlockInformationWrapper>
            {this.renderNumberIOModulesForConfig(this.props.configuration, 0)}
          </BlockInformationWrapper>
          {this.props.configuration?.localRacks?.map((rackConfiguration, idx) => {
            return (
              <BlockInformationWrapper key={idx}>
                {this.renderNumberIOModulesForConfig(rackConfiguration, (idx + 1))}
              </BlockInformationWrapper>
            );
          })}
        </ConfigurationInformation>
      );
    }

    const maxIOModules = this.props.configuration.starterType === possible_starter_selections.PAC ? (this.props.configuration?.rack?.maxIoModules || this.props.configuration.rangeInfo?.maxTotalIOModules) : this.props.configuration.rangeInfo?.maxTotalIOModules || 0;

    return (
      <>
        {maxIOModules > 0 &&
          <ConfigurationInformation orientation={'row'}>
            <ConfigurationInformationTitle>{I10n.getOrDefault(`configuration.general.io_modules.${ioModuleRangeName}`, I10n.get('configuration.general.io_modules'))}</ConfigurationInformationTitle>
            <ConfigurationInformationValue>{this.props.configuration.ioModuleCount()} / {maxIOModules}</ConfigurationInformationValue>
          </ConfigurationInformation>
        }
      </>
    );
  }

  renderContent = (wrapperElement) => {
    const ioModuleRangeName = this.props.configuration.rangeInfo.rangeIOModule;
    const maxComModules = this.props.configuration.rangeInfo?.maxComModules || 0;
    const maxNbIO = this.props.configuration.headModule?.maxNbIO || 0;
    const powerSupplyBuses = this.props.configuration.flatPowerSupplyBuses();
    const worstStatus = this.props.configuration.starterType === possible_starter_selections.PAC ? this._computePacWorstStatus(this.props.configuration) : this._computeBusesWorstStatus(powerSupplyBuses);

    return (
      <>
        {(this.props.configuration.starterType === possible_starter_selections.PLC_IO) &&
          maxComModules > 0 &&
          <ConfigurationInformation orientation={'row'}>
            <ConfigurationInformationTitle>{I10n.getOrDefault(`configuration.general.com_modules.${ioModuleRangeName}`, I10n.get('configuration.general.com_modules'))}</ConfigurationInformationTitle>
            <ConfigurationInformationValue>{this.props.configuration.comModuleCount()} / {maxComModules}</ConfigurationInformationValue>
          </ConfigurationInformation>
        }
        {this.renderNumberIOModules(ioModuleRangeName)}
        {maxNbIO > 0 && <ConfigurationInformation orientation={'row'}>
          <ConfigurationInformationTitle>{I10n.get(`screen.configuration.general.nb_ios`)}</ConfigurationInformationTitle>
          <ConfigurationInformationValue>{this.props.configuration.nbIOConsumedCount()} / {maxNbIO}</ConfigurationInformationValue>
        </ConfigurationInformation>}
        <ConfigurationInformation orientation={'column'}>
          <ConfigurationInformationTitle>
            {I10n.get('configuration.general.width')}
            {!(this.props.configuration.starterType === possible_starter_selections.PAC) && <ActionIcon
              onClick={() => this.setState(
                { dimensionsShown: !this.state.dimensionsShown },
                () => this.props.onRenderDimensionsChange(this.state.dimensionsShown)
              )}
            >
              <MsxIcons
                name={`eye-${this.state.dimensionsShown ? 'show' : 'hide'}`}
                size="nano"
                color="secondary"
              ></MsxIcons>
            </ActionIcon>}
          </ConfigurationInformationTitle>
          <BlockInformationWrapper>
            {(this.props.configuration?.starterType === possible_starter_selections.PAC) ?
              this.renderPACWidths() : this.props.configuration.widths().map((width, blockIndex) => {
                const blockName = blockIndex === 0 ? I10n.get('configuration.general.block.local') : I10n.getAndReplace('configuration.general.block.remote', blockIndex);
                return (
                  <BlockInformationRow key={blockIndex}>
                    <BlockInformationName>{blockName}</BlockInformationName>
                    <BlockInformationValue>
                      <BlockInformationSubRow>
                        <BlockInformationValue>{formatLengthUnit(width)}</BlockInformationValue>
                      </BlockInformationSubRow>
                    </BlockInformationValue>
                  </BlockInformationRow>
                )
              })}
          </BlockInformationWrapper>
        </ConfigurationInformation>
        {
          (this.props.configuration.starterType === possible_starter_selections.PAC && mc80_range_id !== this.props.configuration?.rangeInfo?.rangeId) ?
            this.renderPACConsumptions(wrapperElement)
            :
            this._shouldRenderConsumption(this.props.configuration.maxConsumptions()) &&
            <ConfigurationInformation>
              <ConfigurationInformationTitle>
                {I10n.getOrDefault(`configuration.general.consumption.${ioModuleRangeName}`, I10n.get('configuration.general.consumption'))}
                <ActionIcon
                  onClick={() => this.setState(
                    { consumptionShown: !this.state.consumptionShown },
                    () => this.props.onConsumptionDisplayChange(this.state.consumptionShown)
                  )}
                >
                  {this.props.configuration.rangeInfo.canShowPowerSupplyBuses() &&
                    <MsxIcons
                      name={`eye-${this.state.consumptionShown ? 'show' : 'hide'}`}
                      size="nano"
                      color="secondary"
                    ></MsxIcons>}
                </ActionIcon>
                {worstStatus.color !== consumptionStatus.normal.color && <IconWrapper color={worstStatus.color}>
                  <DropdownMenu
                    data-clickable
                    toggleElement={<InformationIcon size="small"></InformationIcon>}
                    containerElement={<WarningWrapper>{I10n.get(worstStatus.key)}</WarningWrapper>}
                    boundingElement={wrapperElement}
                  ></DropdownMenu>
                </IconWrapper>}
              </ConfigurationInformationTitle>
              <BlockInformationWrapper>
                {powerSupplyBuses.map((buses, blockIndex) => {
                  const blockName = blockIndex === 0 ? I10n.get('configuration.general.block.local') : I10n.getAndReplace('configuration.general.block.remote', blockIndex);
                  return (
                    <ConsumptionInformationRow key={blockIndex}>
                      <BlockInformationName>{blockName}</BlockInformationName>
                      <BlockInformationValue>
                        {buses.map((bus, busIndex) => {
                          const cons = bus.consumption, maxCons = bus.maxConsumption;
                          const shouldScaleConsumption = (maxCons !== null && maxCons >= 10000.0);
                          const consumptionUnit = shouldScaleConsumption ? 'A' : 'mA';
                          const voltage = busIndex === 0 ? '5' : '24';
                          const labelSuffix = busIndex === 0 ? blockIndex : `${blockIndex}${String.fromCharCode('a'.charCodeAt(0) + busIndex - 1)}`;
                          return (
                            <BlockInformationSubRow key={busIndex}>
                              <BlockInformationCell>{I10n.getAndReplace(`configuration.general.consumption.${ioModuleRangeName}.${voltage}V`, labelSuffix)}</BlockInformationCell>
                              <Gauges currentValue={cons} maxValue={maxCons} color={this._computeGaugeStatus(cons, maxCons).color} text={`${this.processElectricalValue(cons, shouldScaleConsumption)} ${I10n.get('unit.' + consumptionUnit, 'data')}`}></Gauges>
                              <BlockInformationValue>
                                {this.processElectricalValue(maxCons, shouldScaleConsumption)} {I10n.get('unit.' + consumptionUnit, 'data')}
                              </BlockInformationValue>
                            </BlockInformationSubRow>
                          );
                        })}
                      </BlockInformationValue>
                    </ConsumptionInformationRow>
                  )
                })}
              </BlockInformationWrapper>
            </ConfigurationInformation>
        }
      </>
    );
  }

  render = () => {

    return (
      <>
        <ScrollbarContainer ref={this.setWrapperRef}>
          <Scrollbars hideTracksWhenNotNeeded>
            <Container>
              <Title >{I10n.get('screen.configuration_information')}</Title>
              {this.renderContent(this.wrapperElement)}
            </Container>
          </Scrollbars>
        </ScrollbarContainer>
        <MobileContainer ref={this.setMobileWrapperRef}>
          {this.renderContent(this.mobileWrapperElement)}
        </MobileContainer>
      </>

    );
  }
}

export default ConfigurationInformations;
